import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Truck, Wrench, Building2, Hammer, DollarSign, UserPlus, ArrowRight } from "lucide-react";
import { getSummary, getReminders } from "@/lib/api";
import { PageHeader } from "@/components/common";
import DueThisMonth from "@/components/DueThisMonth";
import UnclaimedLeads from "@/components/UnclaimedLeads";
import TruckingRequests from "@/components/TruckingRequests";

const SectionCard = ({ to, icon: Icon, label, lines, testId, accent, tint }) => (
  <Link to={to} data-testid={testId}
    className="group relative bg-white border-2 border-gray-200 p-5 flex flex-col justify-between min-h-[150px] overflow-hidden hover:-translate-y-0.5 transition-transform duration-200"
    style={{ borderTopColor: accent, borderTopWidth: 6 }}>
    <div className="absolute -right-4 -top-4 opacity-[0.06]" style={{ color: accent }}>
      <Icon className="w-28 h-28" strokeWidth={1.5} />
    </div>
    <div className="flex items-center justify-between relative">
      <div className="flex items-center justify-center w-11 h-11 rounded-lg" style={{ backgroundColor: tint, color: accent }}>
        <Icon className="w-6 h-6" strokeWidth={2} />
      </div>
      <ArrowRight className="w-5 h-5 text-gray-300 group-hover:translate-x-1 transition-transform" style={{ color: accent }} />
    </div>
    <div className="relative mt-3">
      <div className="font-head text-xl font-bold uppercase tracking-tight text-gray-900">{label}</div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        {lines.map((l, i) => (
          <div key={i} className="text-sm text-gray-500 leading-none">
            <span className="font-mono-data text-xl mr-1" style={{ color: accent }}>{l.value}</span>{l.label}
          </div>
        ))}
      </div>
    </div>
  </Link>
);

const CARDS = [
  { to: "/trucking", icon: Truck, label: "Trucking", testId: "section-card-trucking", accent: "#16265F", tint: "#E7EBF5" },
  { to: "/fleet", icon: Wrench, label: "Fleet", testId: "section-card-fleet", accent: "#0D9488", tint: "#E1F1EF" },
  { to: "/buildings", icon: Building2, label: "Facilities", testId: "section-card-buildings", accent: "#C24E00", tint: "#FBEBE0" },
  { to: "/north-shop", icon: Hammer, label: "North Shop", testId: "section-card-north-shop", accent: "#7B1FA2", tint: "#F1E6F6" },
  { to: "/sales", icon: DollarSign, label: "Recently Sold", testId: "section-card-sales", accent: "#00913C", tint: "#E1F3E8" },
  { to: "/leads", icon: UserPlus, label: "Leads", testId: "section-card-leads", accent: "#E0A81E", tint: "#FBF2DA" },
];

export default function Overview() {
  const { data: summary } = useQuery({ queryKey: ["summary"], queryFn: getSummary, refetchInterval: 5000 });
  const { data: reminders } = useQuery({ queryKey: ["reminders"], queryFn: getReminders, refetchInterval: 5000 });
  const s = summary || {};
  const facilitiesDue = reminders?.facilities ?? 0;

  const linesFor = {
    "/trucking": [{ value: s.trucking?.total ?? 0, label: "stops" }],
    "/fleet": [{ value: s.vehicles?.need_service ?? 0, label: "need done" }, { value: s.vehicles?.in_shop ?? 0, label: "scheduled" }],
    "/buildings": [{ value: s.workorders?.total ?? 0, label: "tasks" }, { value: facilitiesDue, label: "due" }],
    "/north-shop": [{ value: s.shopjobs?.customer ?? 0, label: "customer" }, { value: s.shopjobs?.internal ?? 0, label: "internal" }],
    "/sales": [{ value: s.sales?.total ?? 0, label: "on board" }, { value: s.sales?.delivery ?? 0, label: "deliveries" }],
    "/leads": [{ value: s.leads?.new ?? 0, label: "new" }, { value: s.leads?.contacted ?? 0, label: "contacted" }, { value: s.leads?.won ?? 0, label: "won" }, { value: s.leads?.lost ?? 0, label: "lost" }],
  };

  return (
    <div data-testid="overview-section">
      <PageHeader title="Dashboard" subtitle="Live operations status across the yard" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 items-start">
        <DueThisMonth title="Due This Month" />
        <TruckingRequests />
        <UnclaimedLeads />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <SectionCard key={c.to} {...c} lines={linesFor[c.to]} />
        ))}
      </div>
    </div>
  );
}
